import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    fetchReports();
  }, [statusFilter]);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      let url = `${API}/admin/reports`;
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (reportId, status, notes = '') => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/reports/${reportId}/status?status=${status}&admin_notes=${encodeURIComponent(notes)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Report ${status} successfully`);
      fetchReports();
      setShowReportModal(false);
    } catch (error) {
      console.error('Failed to update report:', error);
      toast.error('Failed to update report status');
    }
  };

  const viewReportDetails = (report) => {
    setSelectedReport(report);
    setShowReportModal(true);
  };

  const getReasonLabel = (reason) => {
    const labels = {
      fraud: 'Fraudulent Listing',
      inappropriate: 'Inappropriate Content',
      spam: 'Spam or Scam',
      wrong_info: 'Wrong Information',
      duplicate: 'Duplicate Listing',
      other: 'Other'
    };
    return labels[reason] || reason;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status Filter</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Reports</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            <div className="text-right">
              <p className="text-sm text-gray-600">Total Reports</p>
              <p className="text-2xl font-bold text-red-600">{reports.length}</p>
            </div>
          </div>
        </div>

        {/* Reports Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Listing</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Reason</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Reporter</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      {report.listingInfo ? (
                        <div>
                          <p className="font-medium">{report.listingInfo.title}</p>
                          <p className="text-xs text-gray-500">R{report.listingInfo.price?.toLocaleString()}</p>
                        </div>
                      ) : (
                        <span className="text-gray-400">Unknown Listing</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        report.reason === 'fraud' ? 'bg-red-100 text-red-700' :
                        report.reason === 'spam' ? 'bg-orange-100 text-orange-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {getReasonLabel(report.reason)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {report.reporterEmail && report.reporterEmail !== 'anonymous@report.com' ? (
                        report.reporterEmail
                      ) : (
                        <span className="text-gray-400 italic">Anonymous</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        report.status === 'resolved' ? 'bg-green-100 text-green-700' :
                        report.status === 'reviewed' ? 'bg-blue-100 text-blue-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {report.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(report.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => viewReportDetails(report)}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {reports.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg">No reports found</p>
          </div>
        )}

        {/* Report Detail Modal */}
        {showReportModal && selectedReport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white z-10">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <i className="fas fa-flag text-red-600"></i> Report Details
                </h3>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Listing Info */}
                {selectedReport.listingInfo && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold mb-2 text-blue-800">Reported Listing</p>
                    <p className="font-medium">{selectedReport.listingInfo.title}</p>
                    <p className="text-sm text-gray-600">Price: R{selectedReport.listingInfo.price?.toLocaleString()}</p>
                    <p className="text-sm text-gray-600">Owner ID: {selectedReport.listingInfo.ownerId}</p>
                  </div>
                )}

                {/* Report Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Reason</p>
                    <p className="font-medium">{getReasonLabel(selectedReport.reason)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="font-medium capitalize">{selectedReport.status}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Reporter Email</p>
                    <p className="font-medium">
                      {selectedReport.reporterEmail && selectedReport.reporterEmail !== 'anonymous@report.com' 
                        ? selectedReport.reporterEmail 
                        : 'Anonymous'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Reported On</p>
                    <p className="font-medium">{new Date(selectedReport.createdAt).toLocaleString()}</p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <p className="text-sm text-gray-600 mb-1">Description</p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-800">{selectedReport.description}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t bg-gray-50 flex flex-wrap gap-3">
                {selectedReport.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleUpdateStatus(selectedReport.id, 'reviewed', 'Report reviewed by admin')}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                      Mark as Reviewed
                    </button>
                    <button
                      onClick={() => handleUpdateStatus(selectedReport.id, 'resolved', 'Issue resolved, appropriate action taken')}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                    >
                      Mark as Resolved
                    </button>
                  </>
                )}

                {selectedReport.status === 'reviewed' && (
                  <button
                    onClick={() => handleUpdateStatus(selectedReport.id, 'resolved', 'Issue resolved, appropriate action taken')}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                  >
                    Mark as Resolved
                  </button>
                )}

                <button
                  onClick={() => setShowReportModal(false)}
                  className="ml-auto bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
